import { motion } from "framer-motion";
import { Facebook, Youtube, Music2, MapPin } from "lucide-react";
import { SOCIALS, CONTACT_EMAIL } from "@/constants/site";

const EASE = [0.22, 1, 0.36, 1];

const SOCIAL_LINKS = [
  { name: "Facebook", url: SOCIALS.facebook, Icon: Facebook, testId: "footer-social-facebook" },
  { name: "TikTok", url: SOCIALS.tiktok, Icon: Music2, testId: "footer-social-tiktok" },
  { name: "YouTube", url: SOCIALS.youtube, Icon: Youtube, testId: "footer-social-youtube" },
];

export default function Footer() {
  return (
    <footer id="contact" data-testid="footer-section" className="relative overflow-hidden bg-[#0D0B0A] py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 1, ease: EASE }}
          className="font-display text-6xl font-medium italic tracking-tight text-[#F2EFE9] md:text-8xl"
        >
          Umi <span className="text-[#D49A36]">Speaks</span>
        </motion.h2>

        <div className="mt-16 grid gap-12 border-t border-[#D49A36]/15 pt-12 md:grid-cols-3">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: EASE }}
          >
            <p className="mb-4 text-[10px] font-bold uppercase tracking-[0.3em] text-[#D49A36]">Contact</p>
            <a
              data-testid="footer-email-link"
              href={`mailto:${CONTACT_EMAIL}`}
              className="font-display text-2xl italic text-[#F2EFE9] transition-colors duration-300 hover:text-[#D49A36] md:text-3xl"
            >
              {CONTACT_EMAIL}
            </a>
            <p className="mt-4 text-sm leading-relaxed text-[#DCD8D3]/70">
              1:1 virtual sessions in wellness &amp; spiritual consultation — request yours above.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
          >
            <p className="mb-4 text-[10px] font-bold uppercase tracking-[0.3em] text-[#D49A36]">Connect</p>
            <div className="flex flex-col gap-3">
              {SOCIAL_LINKS.map(({ name, url, Icon, testId }) => (
                <a
                  key={testId}
                  data-testid={testId}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.18em] text-[#DCD8D3] transition-colors duration-300 hover:text-[#D49A36]"
                >
                  <Icon className="h-4 w-4 text-[#D49A36]/70 transition-colors duration-300 group-hover:text-[#D49A36]" strokeWidth={1.75} />
                  {name}
                </a>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
          >
            <p className="mb-4 text-[10px] font-bold uppercase tracking-[0.3em] text-[#D49A36]">Rooted in</p>
            <p className="flex items-center gap-2 text-sm text-[#DCD8D3]">
              <MapPin className="h-4 w-4 text-[#8C382A]" strokeWidth={1.75} />
              Mississippi, USA
            </p>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-[#DCD8D3]/70">
              Old wisdom. Living practice. Sacred becoming.
            </p>
          </motion.div>
        </div>

        <div className="mt-20 flex flex-col gap-3 border-t border-[#D49A36]/10 pt-8 text-[11px] uppercase tracking-[0.25em] text-[#DCD8D3]/50 md:flex-row md:items-center md:justify-between">
          <span>© 2026 Umi Speaks</span>
          <span className="text-[#D49A36]/70">Culture · Candor · Community</span>
        </div>
      </div>
    </footer>
  );
}
